/**
 * Profile Service
 * 
 * Handles user profile operations including retrieving and updating personal
 * information and user preferences such as currency, language, and notification
 * settings. Manages address fields and preference synchronization.
 */

import apiRequest from '@/shared/services/apiClient.js';

export const profileService = {
  /**
   * Retrieves the authenticated user's complete profile information.
   * Includes personal details, addresses, and verification status.
   * @returns {Promise<Object>} User profile data
   */
  async getUserProfile() {
    let endpoint = '/api/auth/profile/personal-info';
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const userType = (userData?.usertype || userData?.userType || userData?.user_type || '').toLowerCase();
      if (userType === 'business_contact' || userType === 'business_member') {
        endpoint = '/api/auth/profile/contact-person/personal-info';
      }
    } catch (e) {
      // Ignore parse error
    }

    const data = await apiRequest(endpoint, {
      method: 'GET',
    });

    return data;
  },

  /**
   * Updates the authenticated user's profile information.
   * Maps form field names to API-expected field names for address fields.
   * @param {Object} profileData - Profile data to update including country, city, area, and digital address
   * @returns {Promise<Object>} API response confirming profile update
   */
  async updateProfile(profileData) {
    let endpoint = '/api/auth/profile/personal-info';
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const userType = (userData?.usertype || userData?.userType || userData?.user_type || '').toLowerCase();
      if (userType === 'business_contact' || userType === 'business_member') {
        endpoint = '/api/auth/profile/contact-person/personal-info';
      }
    } catch (e) {}

    const requestBody = JSON.stringify({
      country: profileData.country,
      city: profileData.city,
      area: profileData.area,
      digital_address: profileData.digital_address ?? profileData.digitalAddress,
      residential_address: profileData.residential_address,
      dob: profileData.dob
    });

    const updateData = await apiRequest(endpoint, {
      method: 'PUT',
      body: requestBody,
    });

    return updateData;
  },

  /**
   * Retrieves the authenticated user's preferences including currency, language, and notification settings.
   * @returns {Promise<Object>} User preferences data
   */
  async getUserPreferences() {
    const data = await apiRequest('/api/auth/profile/preferences', {
      method: 'GET',
    });
    return data;
  },

  /**
   * Updates the authenticated user's preferences.
   * Maps camelCase and snake_case field names to API-expected format.
   * @param {Object} preferences - Preferences to update including currency, language, and notification preferences
   * @returns {Promise<Object>} API response confirming preferences update
   */
  async updateUserPreferences(preferences) {
    const payload = {
      currency: preferences.currency,
      language: preferences.language,
      notification_preference: preferences.notification_preference ?? preferences.notificationPreference,
      theme: preferences.theme,
      // Note: timezone field is not supported by the backend schema, so we exclude it from the API call
    };
    const update = await apiRequest('/api/auth/profile/preferences', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return update;
  },
};

export default profileService;
